import React, { useEffect, useState } from "react";
import { TextInputV2 } from "@boi/react-components";

import {
    BackButton,
    HyperLink,
    PrimaryButton
} from "../../simple-buttons/simple-buttons";
import BoiReactMarkdown from "../../boi-markdown/boi-markdown";
import LoadingIcon from "../../loading-icon/loading-icon";
import { PageActionsContainer } from "../../theme/containers";
import AuthFormField from "../../authentication/component/auth-form-field";
import { replaceMergeTagsInString, replacePunctuationCharacters } from "../../forms/helper";
import EmailValidator from "../../forms/rules/email-address";
import { BIFAuthScreenProps } from "../types";
import {
    getLoggedInUser,
    loginUser,
    logoutUser,
    requestVerify
} from "../services";

const validateEmail = (email: string, setEmailError: (email: string) => void) => {
    setEmailError("");
    const emailValidator = EmailValidator({ name: "", type: "email" }, email);
    if (!emailValidator[0]) {
        setEmailError(emailValidator[1]);
    }
};

const validatePin = (pin: string, setPinError: (pin: string) => void) => {
    setPinError("");
    if (pin.length < 4) {
        setPinError("PIN number must be at least 4 characters long");
    }
};

const Verification = ({
    context,
    beforeContent,
    afterContent,
    goBackToPreviousPageOnFirstScreen,
    toLoadCurrentUser,
    emailPlaceholder,
    pinPlaceholder,
    expirationInMinutes,
    onChange,
    email,
    pin,
    setEmail,
    setPin,
    setScreen
}: BIFAuthScreenProps) => {
    const [emailError, setEmailError] = useState<string>("");
    const [pinError, setPinError] = useState<string>("");
    const [isVerified, setIsVerified] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (toLoadCurrentUser || isVerified) {
            const user = getLoggedInUser();
            if (user) {
                setEmail(user.email);
                setPin(user.pin);
                setIsVerified(true);
                onChange && onChange(user.token);
            }
        }
    }, []);

    const verify = async (
        email: string,
        pin: string,
        expirationInMinutes?: string
    ) => {
        setLoading(true);
        return requestVerify(email, pin)
            .then(result => {
                if (result && result.status === 200) {
                    loginUser({
                        email,
                        pin,
                        token: result.data.id
                    }, expirationInMinutes);
                    setIsVerified(true);
                    onChange && onChange(result.data.id);
                    setLoading(false);
                    setScreen("successfulVerification");
                } else {
                    setLoading(false);
                    setScreen("failedVerification");
                }
            })
            .catch(() => {
                setLoading(false);
                setScreen("failedVerification");
            });
    };

    function logout() {
        setIsVerified(false);
        logoutUser();
        setEmail("");
        setPin("");
        onChange && onChange("");
    }

    return (
        <>
            <BoiReactMarkdown>
                {(beforeContent &&
                    replaceMergeTagsInString(
                        beforeContent,
                        context ? context.getValues() : {}
                    )) ||
                    `# What&#39;s your email and pin?`
                }
            </BoiReactMarkdown>
            <AuthFormField
                label={"Email"}
                error={emailError}
                isRequired={true}
                id="email"
                formId="bif-auth">
                <TextInputV2
                    id="input_email"
                    value={email}
                    placeholder={
                        emailPlaceholder || "Example: name@company.ie"
                    }
                    onChange={newValue => {
                        const value = replacePunctuationCharacters(newValue);
                        setEmail(value);
                        validateEmail(value, setEmailError);
                    }}
                    onFocus={() => setEmailError("")}
                    type="email"
                    isReadOnly={loading || isVerified}
                    // remove this aria-label if the Container component has deleted and the label and other props are passed to TextInputV2
                    aria-label="Email"
                />
                {/* <StyledInput
                    as="input"
                    type="email"
                    id="input_email"
                    placeholder={
                        emailPlaceholder || "Example: name@company.ie"
                    }
                    value={email}
                    readOnly={loading || isVerified}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = replacePunctuationCharacters(e.target.value);
                        setEmail(value);
                        validateEmail(value, setEmailError);
                    }}
                    onFocus={() => setEmailError("")}
                /> */}
            </AuthFormField>
            <AuthFormField
                label={"Pin"}
                error={pinError}
                isRequired={true}
                id="pin"
                formId="bif-auth">
                <TextInputV2
                    id="input_password"
                    value={pin}
                    placeholder={
                        pinPlaceholder || "Example: 12345AB"
                    }
                    onChange={newValue => {
                        const value = replacePunctuationCharacters(newValue);
                        setPin(value);
                        validatePin(value, setPinError);
                    }}
                    onFocus={() => setPinError("")}
                    type="password"
                    isReadOnly={loading || isVerified}
                    // remove this aria-label if the Container component has deleted and the label and other props are passed to TextInputV2
                    aria-label="Pin"
                />
                {/* <StyledInput
                    as="input"
                    type="password"
                    id="input_password"
                    placeholder={
                        pinPlaceholder || "Example: 12345AB"
                    }
                    value={pin}
                    readOnly={loading || isVerified}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = replacePunctuationCharacters(e.target.value);
                        setPin(value);
                        validatePin(value, setPinError);
                    }}
                    onFocus={() => setPinError("")}
                /> */}
            </AuthFormField>
            {isVerified && (
                <HyperLink
                    as="a"
                    className="u-display-block u-text-right"
                    onClick={() => logout()}
                    icon=""
                    justifyContent=""
                    iconPosition=""
                >
                    Logout
                </HyperLink>
            )}
            {afterContent && (
                <BoiReactMarkdown>
                    {replaceMergeTagsInString(
                        afterContent,
                        context ? context.getValues() : {}
                    )}
                </BoiReactMarkdown>
            )}
            <PageActionsContainer>
                {goBackToPreviousPageOnFirstScreen && context && (
                    <BackButton onClick={() => context.gotoPreviousPage()}>
                        Back
                    </BackButton>
                )}
                <PrimaryButton
                    disabled={
                        !email || !pin || emailError || pinError || loading
                    }
                    onClick={() => {
                        if (!isVerified && email && pin) {
                            verify(email, pin, expirationInMinutes);
                        } else {
                            isVerified && context && context.gotoNextPage();
                        }
                    }}>
                    {loading ? <LoadingIcon /> : "Continue"}
                </PrimaryButton>
            </PageActionsContainer>
        </>
    );
};

export default Verification;
